import { getResolvedTestType } from "@/lib/tests/test-type";
import { resultRouteSegment } from "@/lib/role-plays/scenario-types";

export function toAppUrl(baseUrl, href) {
  if (!href) return "";
  if (/^https?:\/\//i.test(href)) return href;
  if (!baseUrl) return href;

  const normalizedBase = baseUrl.replace(/\/$/, "");
  const normalizedHref = href.startsWith("/") ? href : `/${href}`;
  return `${normalizedBase}${normalizedHref}`;
}

export function getRolePlayAssignmentHref(assignmentId) {
  return `/dashboard/role-plays/assignments/${assignmentId}`;
}

export function getAdminRolePlayAssignmentHref(assignmentId) {
  return `/workspaces/admin/assignments/${assignmentId}`;
}

export function getAdminRolePlayHref(rolePlayId) {
  return `/workspaces/admin/role-plays/${rolePlayId}`;
}

/** One rep's coaching report, opened from a manager's report table. */
export function getCoachingReportHref(userId) {
  return `/workspaces/reports/coaching-report?userId=${encodeURIComponent(userId)}`;
}

// The test link helpers take either a kind key ("faq", "glossary", "fbiv",
// "test") or a test record whose flags say which kind it is.
function resolveTestType(kindOrTest) {
  return typeof kindOrTest === "string"
    ? kindOrTest
    : getResolvedTestType(kindOrTest);
}

const LEARNER_TEST_BASE = {
  faq: "/faq-tests",
  glossary: "/glossary-tests",
  fbiv: "/fbiv-tests",
};

const ADMIN_TESTS_BASE = {
  faq: "/workspaces/admin/faq-tests",
  glossary: "/workspaces/admin/glossary-tests",
  fbiv: "/workspaces/admin/fbiv-tests",
};

/** Where a learner takes (or resumes) a test assignment. */
export function getTestAssignmentHref(assignmentId, kindOrTest = {}) {
  const base = LEARNER_TEST_BASE[resolveTestType(kindOrTest)];
  return base
    ? `${base}/assignments/${assignmentId}`
    : `/tests/assignments/${assignmentId}`;
}

/** Where a learner reads the result of a finished test assignment. */
export function getTestResultsHref(assignmentId, kindOrTest = {}) {
  const base = LEARNER_TEST_BASE[resolveTestType(kindOrTest)];
  return base
    ? `${base}/results/${assignmentId}`
    : `/tests/assignments/${assignmentId}/results`;
}

/** The admin list a test kind lives in ("Back to all Q&A tests"). */
export function getAdminTestsHref(kindOrTest = {}) {
  return (
    ADMIN_TESTS_BASE[resolveTestType(kindOrTest)] || "/workspaces/admin/tests"
  );
}

/** The admin detail page of one test. */
export function getAdminTestHref(testId, kindOrTest = {}) {
  return `${getAdminTestsHref(kindOrTest)}/${testId}`;
}

/** The learner's Learning Paths page, where self-enrollment happens. */
export function getLearningPathsHref() {
  return "/dashboard/learning-paths";
}

/** One enrolled path's progress page; a learner who is not enrolled cannot open it. */
export function getLearningPathHref(learningPathId) {
  return `${getLearningPathsHref()}/${learningPathId}`;
}

export function getLearningPathContentHref(learningPathId, contentId) {
  return `/learning-paths/${learningPathId}/content/${contentId}`;
}

/**
 * The learner's page for one practice result, for any spelling of its kind
 * (the route slug, the RolePlayType enum, a phone key or `ON_THE_FLY`).
 * `contentLinks.test.js` fails on a hand-built copy of this path.
 */
export function getSimulationResultHref(typeOrSlug, resultId) {
  return `/dashboard/${resultRouteSegment(typeOrSlug) ?? typeOrSlug}/results/${resultId}`;
}

/**
 * Where the simulator sends a learner once their session is saved. A session
 * launched from a learning-path step goes back to that step, which generates
 * the feedback itself (SAL-3250); any other session opens its result page,
 * which keeps the story and assignment it was run from.
 */
export function getPostSimulationHref({
  type,
  resultId,
  storyId,
  assignmentId,
  pathId,
  contentId,
}) {
  if (pathId && contentId) return getLearningPathContentHref(pathId, contentId);
  const params = new URLSearchParams();
  if (storyId) params.set("storyId", storyId);
  if (assignmentId) params.set("assignmentId", assignmentId);
  const query = params.toString();
  return `${getSimulationResultHref(type, resultId)}${query ? `?${query}` : ""}`;
}

export function getAdminLearningPathHref(learningPathId) {
  return `/workspaces/admin/learning-paths/${learningPathId}`;
}

export function getResourceHref(resourceId) {
  return `/resources/${resourceId}`;
}

export function getAdminResourceHref(resourceId) {
  return `/workspaces/admin/resources/${resourceId}`;
}

export function getStoriesHref() {
  return "/stories";
}

export function getStoryEditHref(storyId) {
  return `/stories/${storyId}/edit`;
}
